/* global gtag */
import React, {useState, useEffect} from "react";
import {sendEventToGA} from "./../utils.js";
import axios from "axios";
import {redirect, Link, useLoaderData} from "react-router-dom";
import {getIdlCdasApiCodeSnippet, getPythonCdasApiCodeSnippet, getPythonSunpyApiCodeSnippet} from "./apisnippet_utils.js";
import {Header} from "../layout/header.js";
import {Footer} from "../layout/footer.js";
import {ExpandableText} from "../components/expandabletext.js";
import {FtpInstructionsModal} from "../components/ftpinstructionsmodal.js";
import {HeliocloudS3InstructionsModal} from "../components/helioclouds3instructionsmodal.js";
import {CdaWebModal} from "../components/cdawebmodal.js";
import {HelioviewerModal} from "../components/helioviewermodal/helioviewermodal.js";
import {ReportProblemModal} from "../components/reportproblemmodal.js";
import {GenericBanner} from "../components/genericbanner.js";
import {FolderTabs} from "../components/foldertabs.js";
import {CopyToClipboardButton} from "../components/copytoclipboardbutton.js";
import {formatStartOrEndTime, formatRelativeEnd, HilightedCode, convertNominalTimeToUTC, formatCitationAuthors} from "../utils.js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {library as fontAwesomeLibrary} from "@fortawesome/fontawesome-svg-core";
import {faDownload, faUpRightFromSquare, faPenToSquare, faListAlt, faQuoteLeft, faNetworkWired, faBook, faCircleInfo} from "@fortawesome/free-solid-svg-icons";
import {themes} from "prism-react-renderer";
import datasetPageBanner from "../images/datasetpage-bg.jpg";
import "font-awesome/css/font-awesome.min.css";
import {HELIOCLOUDS3LINK} from './../constants.js';

/**
 * React-router-dom Loader function to load backend endpoint with
 * AJAX, the response from which drives the page.
 *
 * @param params Parameters of the page URL
 */
export async function loader({params}) {
    try {
        const response = await axios.get(
            `/heliodata/app/load_dataset_page/${params.hcutId}`
        );

        return {datasetData: response.data};
    }
    catch (error) {
        /* todo: make custom dataset missing page */
        return redirect("/error");
    }
}


/**
 * React component to display the dataset page.
 */
export function DatasetPageContent() {
    const [isFtpModalOpen, setFtpModalOpen] = useState({});
    const [isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen] = useState({});
    const [isCdaWebModalOpen, setCdaWebModalOpen] = useState({});
    const [isHelioviewerModalOpen, setHelioviewerModalOpen] = useState({});
    const [isReportProblemModalOpen, setReportProblemModalOpen] = useState(false);
    const [cdaSessionState, setCdaSessionState] = useState(null);
    const [helioviewerSessionState, setHelioviewerSessionState] = useState(null);
    const {datasetData} = useLoaderData();
    const toolsContent = getToolsContent(datasetData);
    const aboutTheDataContent = <AboutTheDataContent datasetData={datasetData} handleReportProblem={setReportProblemModalOpen}/>;

    const dataAccessRows = getDataAccessRows(datasetData, isFtpModalOpen, setFtpModalOpen, isCdaWebModalOpen, setCdaWebModalOpen, isHelioviewerModalOpen, setHelioviewerModalOpen, isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen);
    const dataAccessFtpModals = getDataAccessFtpModals(datasetData, isFtpModalOpen, setFtpModalOpen);
    const dataAccessHeliocloudS3Modals = getDataAccessHeliocloudS3Modals(datasetData, isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen);
    const dataAccessCdaWebModals = getDataAccessCdaWebModals(datasetData, isCdaWebModalOpen, setCdaWebModalOpen, cdaSessionState, setCdaSessionState);
    const dataAccessHelioviewerModals = getDataAccessHelioviewerModals(datasetData, isHelioviewerModalOpen, setHelioviewerModalOpen, helioviewerSessionState, setHelioviewerSessionState);
    const reportProblemModal = getReportProblemModal(isReportProblemModalOpen, setReportProblemModalOpen);
    const missionNames = datasetData.missions.map(m => m.shortName).join(", ");

    let firstBreadcrumb = {link: '/datasetlist', text: 'Datasets'};

    if (datasetData.missions.length > 0) {
        firstBreadcrumb = {
            link: `/mission/${datasetData.missions[0].hcutId}`,
            text: datasetData.missions[0].shortName
        };
    }

    const descript = datasetData.bodyText;

    return (
        <div className="dataset-page-main">
          <Header />
          <GenericBanner title={datasetData.title}
                         tagline={`Dataset from ${missionNames}`}
	                 shade={true}
                         extraPadding={true}
                         articleContent={true}
                         bannerImage={datasetPageBanner}
                         breadcrumbLevels={[
                             firstBreadcrumb,
                             {
                                 link: `/dataset/${datasetData.hcutId}`,
                                 text: 'This Dataset'
                             },
                         ]}
          />
          <div className="genericBody">

            <div className="dataset-page-outer-div">
              <div className="dataset-page-description">
                <strong>Dataset Description</strong>
                <br />
                <br />
                <ExpandableText
                  text={descript}
                  showWordCount={200}
                  showLineCount={4} />

              </div>

              <div className="data-access-box">
                <h2>Data Access</h2>
                {dataAccessRows}
              </div>

              {dataAccessFtpModals}
              {dataAccessHeliocloudS3Modals}
              {dataAccessCdaWebModals}
              {dataAccessHelioviewerModals}
              {reportProblemModal}

            </div>

            {datasetData.tools.length > 0 ?
             <div><strong>Related Tools</strong>{toolsContent}</div> : <div />}
          </div>

          {aboutTheDataContent}
          <Footer />
        </div>
    );
}


/**
 * Returns array of JSX elements to display related tools.
 *
 * @param datasetData Object from JSON returend by backend
 * @return array of JSX elements to display related tools
 */
function getToolsContent(datasetData) {
    if (datasetData.tools.length === 0) {
        return <div />;
    }

    const toolsContent = [];

    datasetData.tools.forEach(toolObj => {

    });

    return toolsContent;
}


/**
 * Returns array of JSX elements to display "About the Data" content
 *
 * @param datasetData Object from JSON returend by backend
 * @return array of JSX elements to display about the data content
 */
function AboutTheDataContent(props) {
    const {datasetData, handleReportProblem} = props;
    const [selectedTab, setSelectedTab] = useState("summary");

    // Tabs are hidden (not removed) when their content is unavailable
    const tabs = [
        {key: "summary", label: "Summary", icon: faPenToSquare},
        {key: "variables", label: "Variables", icon: faListAlt,
         hidden: datasetData.variables.length === 0},
        {key: "accessByApi", label: "Access By API", icon: faNetworkWired,
         hidden: ! datasetData.hasCdas && ! datasetData.hasSunpy},
        {key: "citeTheData", label: "Cite the Data", icon: faQuoteLeft},
        {key: "documentation", label: "Documentation", icon: faBook,
         hidden: datasetData.documentation.length === 0},
    ];

    // Decide the visible content based on the selected tab
    var visibleContent;

    if (selectedTab === "summary") {
        visibleContent = <AboutTheDataSummaryContents datasetData={datasetData} />;
    }
    else if (selectedTab === "variables") {
        visibleContent = getAboutTheDataVariableContents(datasetData);
    }
    else if (selectedTab === "citeTheData") {
        visibleContent = getAboutTheDataCitationContents(datasetData);
    }
    else if (selectedTab === "accessByApi") {
        visibleContent = <AboutTheDataApiContents datasetData={datasetData} />;
    }
    else if (selectedTab === "documentation") {
        visibleContent = getAboutTheDataDocumentationContents(datasetData);
    }

    fontAwesomeLibrary.add(faPenToSquare);
    fontAwesomeLibrary.add(faListAlt);
    fontAwesomeLibrary.add(faQuoteLeft);
    fontAwesomeLibrary.add(faNetworkWired);
    fontAwesomeLibrary.add(faBook);

    // Return main JSX content with visibleContent embedded
    return (
        <div>
          <div id="aboutTheDataAnchor" className="about-the-data-header">
            About the Data

            <div
              className="about-the-data-report-button"
              onClick={() => handleReportProblem({})}>
              Report Problem
            </div>
          </div>

          <FolderTabs
            idPrefix="aboutTheData"
            tabs={tabs}
            selected={selectedTab}
            onSelect={setSelectedTab}
            panelClassName="about-the-data-visible-content"
            scrollToId="aboutTheDataAnchor">
            {visibleContent}
          </FolderTabs>

        </div>
    );
}


/**
 * Gets the summary tab contents of the "About the Data" section.
 *
 * @param props.datasetData Response from backend describing dataset.
 * @return JSX element
 */
function AboutTheDataSummaryContents(props) {
    const {datasetData} = props;
    const rows = [["Product ID", datasetData.hcutId]];

    if(!datasetData) {
        return (<div>Nothing to display</div>)
    }

    if (datasetData.missions.length > 0) {
        const missionContent = [];

        datasetData.missions.forEach(
            missionData => {
                missionContent.push(
                    <Link to={`/mission/${missionData.hcutId}`} key={`mission-${missionData.hcutId}`}>
                      {missionData.shortName}
                    </Link>
                );
                missionContent.push(<span key={`region-comma-${missionData.hcutId}`}>, </span>);
            }
        );

        missionContent.pop(); // remove last comma

        rows.push(["Mission/Observatory", missionContent]);
    }

    if (datasetData.regions.length > 0) {
	const regionContent = [];

	datasetData.regions.forEach(regionObj => {
	    regionContent.push(
		<Link to={`/region/${regionObj.regionName}`} key={`/region/${regionObj.regionName}`}>
  		    {regionObj.regionTitle}
		</Link>
            );
            regionContent.push(<span key={`region-comma-${regionObj.regionName}`}>, </span>);
	    regionContent.push(<br key={`region-br-${regionObj.regionName}`} />);
	});

	regionContent.pop();  // remove last <br>
	regionContent.pop();  // remove last comma

        rows.push(["Regions", regionContent]);
    }

    if (datasetData.formats.length > 0) {
        rows.push(["Available Formats", datasetData.formats.join(", ")]);
    }

    if (datasetData.cadence) {
        rows.push(["Time Resolution", datasetData.cadenceHuman]);
    }

    if (datasetData.nominalStartTime) {
        const formattedTime = formatStartOrEndTime(datasetData.nominalStartTime);
        rows.push(["Start Time", formattedTime]);
    }

    if (datasetData.nominalRelativeEnd) {
        rows.push(["End Time", formatRelativeEnd(datasetData.nominalRelativeEnd)]);
    }
    else if (datasetData.nominalEndTime) {
        const formattedTime = formatStartOrEndTime(datasetData.nominalEndTime);
        rows.push(["End Time", formattedTime]);
    }

    if (datasetData.authors) {
        rows.push(["Authors", formatCitationAuthors(datasetData.authors)]);
    }

    if (datasetData.publishedBy) {
        rows.push(["Published By", datasetData.publishedBy]);
    }

    if (datasetData.doi) {
        rows.push([
            "DOI",
            <a href={datasetData.doi} target="_Blank" rel="noreferrer">
              {datasetData.doi}
            </a>
        ]);
    }

    if (datasetData.spaseUrl) {
        rows.push([
            "SPASE URL",
            <a
                href={datasetData.spaseUrl}
                target="_blank"
                rel="noopener noreferrer"
            >
                {datasetData.spaseUrl}
            </a>
        ]);
    }

    return (
        <div>
          {rows.map((arr,i) => {
              return (
                  <div className="about-the-data-summary-field-pair" key={i + 80000}>
                    <span className="about-the-data-summary-field-name">
                      {arr[0]}
                    </span>

                    <span className="about-the-data-summary-field-value">
                      {arr[1]}
                    </span>
                  </div>
              );
          })}
        </div>
    );
}


/**
 * Gets the "Access By API" tab contents of the "About the Data" section,
 * which displays code snippets for downloading the data on the fly.
 * @param props.datasetData Response from backend describing dataset.
 * @return JSX element to display in the tab contents
 */
export function AboutTheDataApiContents(props) {
    const {datasetData} = props;
    const [hapiOptions, setHapiOptions] = useState([]);
    const [selectedApi, setSelectedApi] = useState(null);

    useEffect(() => {
        if (!datasetData.hasHapi) return;

        const fetchHapiOptions = async () => {
            try {
                const { data } = await axios.get("/heliodata/app/hapi_language_options");
                setHapiOptions(data);

                if (!datasetData.hasCdas && !datasetData.hasSunpy) {
                    setSelectedApi(`hapi ${data[0].value}`);
                }
            } catch (err) {
                console.error("Failed to fetch HAPI script options:", err);
            }
        };

        fetchHapiOptions();
    }, [datasetData.hasHapi]);

    // Determine which API options to show
    const apiOptions = [];
    const apiOptionsJsx = [];

    if (datasetData.hasCdas) {
	apiOptions.push("python cdas");
	apiOptions.push("python idl");
	apiOptionsJsx.push(<option key="python-cdas" value="python cdas">Python (SPDF CDAS)</option>);
	apiOptionsJsx.push(<option key="idl-cdas" value="idl cdas">IDL (SPDF CDAS)</option>);
    }
    else if (datasetData.hasSunpy) {
	apiOptions.push("python sunpy");
	apiOptionsJsx.push(<option key="python-sunpy" value="python sunpy">Python (SunPy)</option>);
    }

    console.log(`The dataset... ${JSON.stringify(datasetData)}`);

    if (datasetData.hasHapi) {
        hapiOptions.forEach(({ label, value }) => {
            apiOptions.push(`hapi ${value}`);
            apiOptionsJsx.push(
                <option key={value} value={`hapi ${value}`}>
                    {label} (HAPI)
                </option>
            );
        });
    }

    // Determine what is visible
    let visibleContent;
    const effectiveSelected = selectedApi ?? apiOptions[0];

    if (effectiveSelected === "python cdas") {
        visibleContent = <CdawebPythonInstructions datasetData={datasetData} />;
    } else if(effectiveSelected === "python sunpy") {
        visibleContent = <SunPyInstructions datasetData={datasetData} />;
    } else if (effectiveSelected === "idl cdas") {
        visibleContent = <CdawebIdlInstructions datasetData={datasetData}/>;
    } else if (effectiveSelected?.startsWith("hapi")) {
        visibleContent = <HAPIInstructions datasetData={datasetData} language={effectiveSelected?.split(" ")[1]}/>
    }

    return (
        <div className="access-by-api-contents">
          <p>
            <strong>API Option</strong>:&nbsp;
            <select
              className="access-by-api-lang-input"
              defaultValue={effectiveSelected ?? ""}
              onChange={event => setSelectedApi(event.target.value)}>
	      {apiOptionsJsx}
            </select>
          </p>
          {visibleContent}
        </div>
    );
}


/**
 * Gets the variables tab contents of the "About the Data" section.
 * @param datasetData Response from backend describing dataset.
 * @return JSX element
 */
function getAboutTheDataVariableContents(datasetData) {
    return (
        <div>
          <table className="variables-table">
            <thead>
              <tr>
                <th align="left" className="variables-table-longname">Variable</th>
                <th align="left">Name</th>
                <th align="left">Units</th>
              </tr>
            </thead>
            <tbody>
                {datasetData.variables.map((variable, idx) => {
                  return (
                      <tr key={variable.shortName} className={idx % 2 === 0 ? "variables-table-shade" : ""}>
                        <td>{variable.longName}</td>
                        <td>{variable.shortName}</td>
                        <td>{variable.units}</td>
                      </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
    );
}


/**
 * Gets the data citation tab contents of the "About the Data" section.
 * @param datasetData Response from backend describing dataset.
 * @return JSX element
 */
function getAboutTheDataCitationContents(datasetData) {
    let url = (datasetData.spaseId) ?
        datasetData.spaseId.replace("spase://", "https://spase-metadata.org/")
	: window.location.href;
    const visibleContent = [];

    // Add text citation section. The copied text is built from the same
    // values as the rendered excerpt so the two always agree.
    const hasFullCitation = (datasetData.doi || datasetData.spaseUrl) && datasetData.authors;
    const currentYear = new Date().getFullYear();
    const publicationYear = datasetData.publicationDateYear || currentYear;
    const citationLink = hasFullCitation ? (datasetData.doi || datasetData.spaseUrl) : url;
    const authors = formatCitationAuthors(datasetData.authors) || "";
    const authorsSentence = authors.endsWith(".") ? authors : authors + ".";

    const citationText = hasFullCitation
          ? `${authorsSentence} "${datasetData.title}." [Dataset]. ${datasetData.publishedBy} (${publicationYear}). ${citationLink}`
          : `"${datasetData.title}" [Dataset]. (${currentYear}). ${citationLink}`;

    visibleContent.push(
        <div key="citation-text" className="citationBlock">
          <p><strong>The following excerpt can be used to cite this dataset:</strong></p>

          <p style={{paddingLeft: "20px"}}>
            {hasFullCitation
             ? <>
                 {authorsSentence} "{datasetData.title}." {" [Dataset]. "}
                 <em>{datasetData.publishedBy}</em> ({publicationYear}).&nbsp;
               </>
             : <>
                 "{datasetData.title}" {" [Dataset]."}
                 ({currentYear}). &nbsp;
               </>}
            <a href={citationLink} target="_Blank" rel="noreferrer">{citationLink}</a>
          </p>

          <div className="citationActions">
            <CopyToClipboardButton
              text={citationText}
              eventLabel="dataset_citation_text_copied__button"
              eventType="datasets"
              eventValue={datasetData.title} />
          </div>
        </div>
    );

    // Add BibTex Section
    let bibTexId = datasetData.hcutId.replace(/(_|\.)/g, '');
    const bibTexAuthor = formatCitationAuthors(datasetData.authors) || datasetData.publishedBy || undefined;

    const lines = [];
    lines.push(`title = {${datasetData.title}}`);

    if (bibTexAuthor) {
        lines.push(`author = {${bibTexAuthor}}`);
    }

    if (datasetData.doi) {
        lines.push(`doi = {${datasetData.doi}}`);
    } else {
        lines.push(`url = {${url}}`);
    }

    lines.push(`year = {${publicationYear}}`);

    if (datasetData.publishedBy) {
        lines.push(`publisher = {${datasetData.publishedBy}}`);
    }

    const bibTexText = `@dataset{${bibTexId},\n`
          + lines.map((line) => "  " + line).join(",\n")
          + "\n}";

    visibleContent.push(
        <div key="citation-bibtex" className="citationBlock">
              <p><strong>The following BibTeX can also be used to cite this dataset:</strong></p>

              <p style={{paddingLeft: "20px"}}>
                <code>
                  @dataset&#123;{bibTexId},<br />
                  {lines.map(
                      (line, idx) =>
                          <span key={idx}>
                            {idx > 0 ? <span>,<br /></span> : ""}
                            &nbsp;&nbsp;{line}
                          </span>
                  )}
                  <br />
                  &#125;
                </code>
              </p>

              <div className="citationActions">
                <CopyToClipboardButton
                  text={bibTexText}
                  eventLabel="dataset_citation_bibtex_copied__button"
                  eventType="datasets"
                  eventValue={datasetData.title} />
              </div>
        </div>
    );

    return visibleContent;
}


/**
 * Gets the documentation tab contents of the "About the Data" section.
 * @param datasetData Response from backend describing dataset.
 * @return JSX element
 */
function getAboutTheDataDocumentationContents(datasetData) {
    const docItems = [];
    fontAwesomeLibrary.add(faUpRightFromSquare);

    datasetData.documentation.forEach(docData => {
        docItems.push(
            <li key={docData.url}>
              <a href={docData.url} alt={docData.altText} target="_blank" rel="noreferrer">
                {docData.name}
                &nbsp;
                <FontAwesomeIcon
                  style={{fontSize: "11px", marginLeft: "4px"}}
                  icon={faUpRightFromSquare} />
              </a>
              <br />
            </li>
        );
    });

    return (
        <div>
          <p>The following documentation is available:</p>

          <ul className="about-the-data-documentation-list">
            {docItems}
          </ul>
        </div>
    );
}


/**
 * Get Data Access FTP Modal Dialogs.
 *
 * @param datasetData Response from backend describing dataset.
 * @param isFtpModalOpen Method for accessing whether modal is visible, by ftp url.
 * @param setFtpModalOpen Method for setting state
 * @return JSX element
 */
function getDataAccessFtpModals(datasetData, isFtpModalOpen, setFtpModalOpen) {
    const ftpModals = (
        datasetData
            .dataAccessMethods
            .map(accessMethod => accessMethod.url)
            .filter(accessMethod => accessMethod.toLowerCase().startsWith("ftp"))
            .map((ftpUrl, i) =>{
                 return <FtpInstructionsModal
                   ftpUrl={ftpUrl}
                   key={i + 2000}
                   isFtpModalOpen={isFtpModalOpen[ftpUrl]}
                   setFtpModalOpen={setFtpModalOpen} />
                 })
    );

    return ftpModals;
}


/**
 * Get Data Access Heliocloud S3 Modal Dialogs.
 *
 * @param datasetData Response from backend describing dataset.
 * @param isHeliocloudS3ModalOpen Method for accessing whether modal is visible.
 * @param setHeliocloudS3ModalOpen Method for setting state
 * @return JSX element
 */
function getDataAccessHeliocloudS3Modals(datasetData, isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen) {
    const hcS3Modals = (
        datasetData
            .dataAccessMethods
            .map(accessMethod => accessMethod.url)
            .filter(accessMethod => accessMethod.toLowerCase().startsWith(HELIOCLOUDS3LINK))
            .map((hcS3Url, i) =>{
                 return <HeliocloudS3InstructionsModal
                   heliocloudS3ProductUrl={hcS3Url}
                   productKey={datasetData.cdasId}
                   key={i + 3000}
                   isHeliocloudS3ModalOpen={isHeliocloudS3ModalOpen[hcS3Url]}
                   setHeliocloudS3ModalOpen={setHeliocloudS3ModalOpen} />
                 })
    );

    return hcS3Modals;
}


/**
 * Get Plots via  CdaWeb Modal.
 *
 * @param datasetData Response from backend describing dataset.
 * @param isCdaWebModalOpen Method for accessing whether modal is visible.
 * @param setCdaWebModalOpen Method for setting state
 * @return JSX element
 */
function getDataAccessCdaWebModals(datasetData, isCdaWebModalOpen, setCdaWebModalOpen, cdaSessionState, setCdaSessionState) {
    const cdaWebModals = (
        datasetData
            .dataAccessMethods
            .map((accessMethod) => {return {url: accessMethod.url, name: accessMethod.name }})
            .filter(accessMethod => accessMethod.name.toLowerCase().includes("visualize with cdaweb"))
            .map((accessMethod, i) => {
                 return <CdaWebModal
                   title = {datasetData.title}
                   key={i + 12000}
                   variables = {datasetData.variables}
                   url={accessMethod.url}
                   strTime={datasetData.nominalStartTime}
                   stpTime={datasetData.nominalEndTime
                            || (datasetData.nominalRelativeEnd
                                && convertNominalTimeToUTC(datasetData.nominalRelativeEnd)
                               ) || null}
                   cdasId={datasetData.cdasId}
                   isCdaWebModalOpen={isCdaWebModalOpen[accessMethod.url]}
                   setCdaWebModalOpen={setCdaWebModalOpen}
                   sessionState={cdaSessionState}
                   setSessionState={setCdaSessionState} />
                 })
    );

    return cdaWebModals;
}


/**
 * Get visual data from helioviewer and displays it in a Modal.
 *
 * @param datasetData Response from backend describing dataset.
 * @param isCdaWebModalOpen Method for accessing whether modal is visible.
 * @param setCdaWebModalOpen Method for setting state
 * @return JSX element
 */
function getDataAccessHelioviewerModals(datasetData, isHelioviewerModalOpen, setHelioviewerModalOpen, helioviewerSessionState, setHelioviewerSessionState) {
    const helioviewerSelection = {
                        type:  (datasetData.helioviewerInstrument && "Instrument") ||
                               (datasetData.helioviewerEnergyBand && "EnergyBand") ||
                               "Measurement" ,
                        sourceId: datasetData.helioviewerSourceId,
                        instrument: datasetData.helioviewerInstrument,
                        measurement: datasetData.helioviewerMeasurement,
                        energyBand: datasetData.helioviewerEnergyBand,
                        reconstruction: datasetData.helioviewerReconstruction,
                        detector: datasetData.helioviewerDetector,
                        filter: datasetData.helioviewerFilter,
                        filterWheel1: datasetData.helioviewerFilterWheel1,
                        filterWheel2: datasetData.helioviewerFilterWheel2
                      };

    const helioviewerModals = [
        <HelioviewerModal
            title = {datasetData.title}
            key = {datasetData.helioviewerId}
            id = {datasetData.helioviewerId}
            mission = {datasetData.missions[0]?.shortName || datasetData.helioviewerMission}
            helioviewerSelection = {helioviewerSelection}
            isHelioviewerModalOpen={isHelioviewerModalOpen[datasetData.title]}
            setHelioviewerModalOpen={setHelioviewerModalOpen} />
    ];

    return helioviewerModals;
}


/**
 * Get Plots via  CdaWeb Modal.
 *
 * @param datasetData Response from backend describing dataset.
 * @param isCdaWebModalOpen Method for accessing whether modal is visible.
 * @param setCdaWebModalOpen Method for setting state
 * @return JSX element
 */
function getReportProblemModal( isReportProblemModalOpen, setReportProblemModalOpen) {
    const reportProblemModal = <ReportProblemModal
                                  isReportProblemModalOpen = {isReportProblemModalOpen}
                                  setReportProblemModalOpen = {setReportProblemModalOpen} />;
    return reportProblemModal;
}


/**
 * Gets the data access panel button links. This comes from the
 * backend response "dataAccessMethods" branch.
 * @param datasetData Response from backend describing dataset.
 * @param isFtpModalOpen Method for accessing whether modal is visible, by ftp url.
 * @param setFtpModalOpen Method for setting state
 * @return JSX element
 */
function getDataAccessRows(datasetData, isFtpModalOpen, setFtpModalOpen, isCdaWebModalOpen, setCdaWebModalOpen, isHelioviewerModalOpen, setHelioviewerModalOpen, isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen) {

    const numFtpUrls = (
        datasetData
            .dataAccessMethods
            .map(accessMethod => accessMethod.url)
            .filter(accessMethod => accessMethod.toLowerCase().startsWith("ftp"))
            .length
    );

    const numHeliocloudS3Urls = (
        datasetData
            .dataAccessMethods
            .map(accessMethod => accessMethod.url)
            .filter(accessMethod => accessMethod.toLowerCase().startsWith(HELIOCLOUDS3LINK))
            .length
    );

    const nonCdaDataAccessRows = [];
    const cdaDataAccessRows = [];
    const helioviewerDataAccessRows = [];

    fontAwesomeLibrary.add(faUpRightFromSquare);
    fontAwesomeLibrary.add(faCircleInfo);
    fontAwesomeLibrary.add(faDownload);

    // Handle helioviewer
    if (datasetData.hasHelioviewer) {
        const newObj = {};
        newObj[datasetData.title]= true;
        helioviewerDataAccessRows.push(
            <div
              className="data-access-box-child data-access-box-helioviewer"
              onClick={() => setHelioviewerModalOpen({...isHelioviewerModalOpen, ...newObj})}
              key={helioviewerDataAccessRows.length + 1000}
              title={datasetData.title}>
              {"Visualize Data"}&nbsp;{<FontAwesomeIcon icon={faDownload} />}
            </div>
        );
    }

    // Handle ftp, https, and api
    datasetData.dataAccessMethods.forEach(accessMethod => {
        let onClick, label, icon;

        if (accessMethod.url.toLowerCase().startsWith("ftp")) {
            const newObj = {};
            newObj[accessMethod.url]= true;
            onClick = () => {sendEventToGA("dataset_access__ftp_modal__button", "datasets", datasetData.title); setFtpModalOpen({...isFtpModalOpen, ...newObj})};

            if (numFtpUrls === 1) {
                label = "FTP Download";
            } else {
                const ftpUrlObj = new URL(accessMethod.url.replace('ftp', 'http'));
                label = <span>FTP Download<br /> ({ftpUrlObj.hostname})</span>;
            }
            icon = <FontAwesomeIcon icon={faCircleInfo} />;
        } else if(accessMethod.name.toLowerCase().includes("visualize with cdaweb")) {
            const newObj = {};
            newObj[accessMethod.url]= true;

            label = "Get Data";
            onClick = () => {sendEventToGA("dataset_access__cda_modal__button", "datasets", datasetData.title); setCdaWebModalOpen({...isCdaWebModalOpen, ...newObj})};

            icon = <FontAwesomeIcon icon={faDownload} />
        } else if (accessMethod.url.toLowerCase().startsWith(HELIOCLOUDS3LINK)) {
            const newObj = {};
            newObj[accessMethod.url]= true;
            onClick = () => {sendEventToGA("dataset_access__heliocloud_s3_modal__button", "datasets", datasetData.title); setHeliocloudS3ModalOpen({...isHeliocloudS3ModalOpen, ...newObj})};

            if (numHeliocloudS3Urls === 1) {
                label = "HelioCloud S3 Download";
            } else {
                const hcS3UrlObj = new URL(accessMethod.url);
                label = <span>HelioCloud S3 Download<br /> ({hcS3UrlObj.hostname})</span>;
            }
            icon = <FontAwesomeIcon icon={faCircleInfo} />;
        } else {
            onClick = () => {sendEventToGA(`dataset_access__https__button`, "datasets", datasetData.title); window.open(accessMethod.url)};
            label = accessMethod.name;
            icon = <FontAwesomeIcon icon={faUpRightFromSquare} />;
        }

        const mouseDownHandler = (event) => {
            // Middle click
            if (event.button === 1) {
                window.open(accessMethod.url, "_blank");
                window.focus();
            }
        };

        if(accessMethod.name.toLowerCase().includes("visualize with cdaweb")) {
            cdaDataAccessRows.push(
                <div
                  className="data-access-box-child data-access-box-cda"
                  onMouseDown={mouseDownHandler}
                  onClick={onClick}
                  key={cdaDataAccessRows.length + 50000}
                  title={accessMethod.altText}>
                  {label}&nbsp;{icon}
                </div>
            );
        } else {
            nonCdaDataAccessRows.push(
                <div
                  className="data-access-box-child"
                  onMouseDown={mouseDownHandler}
                  onClick={onClick}
                  key={nonCdaDataAccessRows.length + 10000}
                  title={accessMethod.altText}>
                  {label}&nbsp;{icon}
                </div>
            );
        }
    });

    return [...cdaDataAccessRows,...helioviewerDataAccessRows,...nonCdaDataAccessRows];
}

/**
 * SunPy instructions for Python
 *
 * @param props.datasetData (Backend response describing datset)
 * @return JSX
 */
function SunPyInstructions(props) {
    const {datasetData} = props;
    const [state, setState] = useState({pythonCodeCopied: false});
    let pythonCode = getPythonSunpyApiCodeSnippet(
                       datasetData.nominalStartTime,
                       datasetData.wavelengths[0] ? datasetData.wavelengths[0] : null,
                       datasetData.sunpyInstrument);

    const copyPythonToClipboard = () => {
        navigator.clipboard.writeText(pythonCode);

        setState(prevState => ({ ...prevState, pythonCodeCopied: true }));

        setTimeout(() => {
            setState(prevState => ({ ...prevState, pythonCodeCopied: false }));
        }, 3000);
    };

    return (
        <div className="access-by-api-section">
          <p>
            This data can be accessed through the &nbsp;
            <a href="https://docs.sunpy.org/en/stable/generated/api/sunpy.net.Fido.html#sunpy.net.Fido" target="_blank" rel="noreferrer">
              FIDO API
            </a> in&nbsp;
            <a href="https://pypi.org/project/sunpy/" target="_blank" rel="noreferrer">
              SunPy
            </a>.
            To install the prerequisite modules, run this command:
          </p>
          <br />
          <code className="access-by-api-code">$ pip install sunpy requests beautifulsoup4 python-dateutil lxml zeep drms</code>
          <p>&nbsp;</p>
          <p>The following code can be used to load the first map in a time range and plot it. More information
             about FIDO can be found on the SunPy page&nbsp;
             <a href="https://docs.sunpy.org/en/stable/tutorial/acquiring_data/index.html" target="_blank" rel="noreferrer">Acquiring Data&nbsp;</a>,
             and more information about Map objects be found on&nbsp;
             <a href="https://docs.sunpy.org/en/stable/tutorial/maps.html" target="_blank" rel="noreferrer">Maps tutorial</a>.
             Information related to available instruments can be found on the&nbsp;
             <a href="https://docs.sunpy.org/en/stable/generated/api/sunpy.net.attrs.Instrument.html" target="_blank" rel="noreferrer">SunPy's Instrument page</a>.</p>
          <br />
          <button onClick = {() => { sendEventToGA("dataset_access__sunpy_instructions_copied__button", "datasets", datasetData.title); copyPythonToClipboard()}} className= {state.pythonCodeCopied ? "dataset-api-copied-button": "dataset-api-copy-button"}>
            {state.pythonCodeCopied ? "Copied" : "Copy to Clipboard"}
          </button>

          <HilightedCode
            className="access-by-api-code"
            language="python"
            code={pythonCode}
            theme={themes.oneLight} />
        </div>
    );
}

/**
 * API Access instructions for IDL
 *
 * @param props.datasetData (Backend response describing datset)
 * @return JSX
 */
function CdawebIdlInstructions(props) {
    const {datasetData} = props;
    const [state, setState] = useState({idlCodeCopied: false});
    // eslint-disable-next-line
    const [filterText, setFilterText] = useState('');
    // eslint-disable-next-line
    const [filteredVariables, setFilteredVariables] = useState([]);

    useEffect(() => {
        const filterVariables = () => {
            if (filterText.trim() === '') {
                return datasetData.variables
                    .filter(varData => varData.showInApiSnippet)
                    .map(varData => varData.shortName);
            }

            const filterTerms = filterText.split(',').map(term => term.trim().toLowerCase());
            return datasetData.variables
                .filter(varData => varData.showInApiSnippet)
                .map(varData => varData.shortName)
                .filter(varName => filterTerms.some(term => varName.toLowerCase().includes(term)));
        };

        setFilteredVariables(filterVariables());
    }, [filterText, datasetData.variables]);

    const idlCode = getIdlCdasApiCodeSnippet(datasetData.cdasId);

    const copyIdlToClipboard = () => {
        navigator.clipboard.writeText(idlCode);
        setState(prevState => ({ ...prevState, idlCodeCopied: true }));

        setTimeout(() => {
            setState(prevState => ({ ...prevState, idlCodeCopied: false }));
        }, 3000);


    };

    return (
        <div className="access-by-api-section">
          See also: <a href="https://cdaweb.gsfc.nasa.gov/WebServices/REST/jupyter/CdasWsIdlExample.html" target="_blank" rel="noreferrer">Notebook Example</a>

          <div>
            <button onClick = {()=> {sendEventToGA("dataset_access__cdaweb_idl_instructions_copied__button", "datasets",  datasetData.title); copyIdlToClipboard()}} className={state.idlCodeCopied ? "dataset-api-copied-button": "dataset-api-copy-button"}>
              {state.idlCodeCopied ? "Copied" : "Copy to Clipboard"}
            </button>
          </div>

          <HilightedCode
            className="access-by-api-code"
            language="css"   /* No IDL supported, but CSS works decently */
            code={idlCode}
            theme={themes.oneLight} />
        </div>
    );
}


/**
 * API Access instructions for Python
 *
 * @param props.datasetData (Backend response describing datset)
 * @return JSX
 */
function CdawebPythonInstructions(props) {
    const {datasetData} = props;
    const [state, setState] = useState({pythonCodeCopied: false});

    const pythonCode = getPythonCdasApiCodeSnippet(datasetData.cdasId);
    const copyPythonToClipboard = () => {
        navigator.clipboard.writeText(pythonCode);
        setState(prevState => ({ ...prevState, pythonCodeCopied: true }));
        setTimeout(() => {
            setState(prevState => ({ ...prevState, pythonCodeCopied: false }));
        }, 3000);
    };

    return (
        <div className="access-by-api-section">
          <p>
            This data can be accessed through the &nbsp;
            <a href="https://pypi.org/project/cdasws/" target="_blank" rel="noreferrer">
              CDAS Python API
            </a>.
            To install the prerequisite modules, there are two options. Also available are <a href="https://mybinder.org/v2/gh/berniegsfc/cdasws-notebooks/main" target="_Blank" rel="noreferrer">notebook examples</a>.
          </p>

          <span>Option 1:</span>
          <code className="access-by-api-code">$ pip install spacepy cdasws</code><br />

          <span>Option 2:</span>
          <code className="access-by-api-code">$ pip install xarray cdflib cdasws</code><br />

          <p>The following code can be used to load the data on the fly:</p>
          <br />

          <div>
            <button onClick= {() => {sendEventToGA("dataset_access__cdaweb_python_instructions_copied__button", "datasets", datasetData.title); copyPythonToClipboard()}} className={state.pythonCodeCopied ? "dataset-api-copied-button": "dataset-api-copy-button"}>
              {state.pythonCodeCopied ? "Copied" : "Copy to Clipboard"}
            </button>
          </div>

          <HilightedCode
            className="access-by-api-code"
            language="python"
            code={pythonCode}
            theme={themes.oneLight} >
          </HilightedCode>
        </div>
    );
}


/**
 * API Access instructions for HAPI
 *
 * @param props.datasetData (Backend response describing datset)
 * @param props.language {string} Selected language value from HAPI script options
 *                                (e.g. "python", "julia", "matlab", "javascript",
 *                                "idl", "idl-spedas", "python-spedas",
 *                                "python-kamodo", "python-kamodo-alt",
 *                                "autoplot", "curl", "wget")
 * @return JSX
 */
function HAPIInstructions(props) {
    const {datasetData, language} = props;
    const [state, setState] = useState({ hAPICodeCopied: false });
    const [hAPICode, setHAPICode] = useState("");

    useEffect(() => {
        const fetchCodeSnippet = async () => {
            try {
                const { data } = await axios.get("/heliodata/app/hapi_code_snippet", {
                    params: {
                        server: datasetData.hapiUrl,
                        dataset: datasetData.hapiId,
                        format: language,
                    }
                });
                setHAPICode(data);
            } catch (err) {
                setHAPICode(`# Failed to load code snippet.\n${err.response?.data}`);
            }
        };

        fetchCodeSnippet();
    }, [language, datasetData.hapiUrl, datasetData.hapiId]);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(hAPICode);
        setState(prevState => ({ ...prevState, hAPICodeCopied: true }));
        setTimeout(() => {
            setState(prevState => ({ ...prevState, hAPICodeCopied: false }));
        }, 3000);
    };

    return (
        <div className="access-by-api-section">
            <p>
                This data can be accessed through the&nbsp;
                <a href="https://hapi-server.org/servers" target="_blank" rel="noreferrer">
                    HAPI API
                </a>.
            </p>
            <p>The following code, which is pulled from the HAPI site, can be used to load the data on the fly:</p>
            <br/>
            {hAPICode && !hAPICode.toLowerCase().includes("failed to load code snippet") &&
              <div>
                <button
                    onClick={() => {
                        sendEventToGA("dataset_access__cdaweb_hapi_instructions_copied__button", "datasets", datasetData.title);
                        copyToClipboard();
                    }}
                    className={state.hAPICodeCopied ? "dataset-api-copied-button" : "dataset-api-copy-button"}>
                    {state.hAPICodeCopied ? "Copied" : "Copy to Clipboard"}
                </button>
              </div>

            }
            <div className="access-by-api-wrap">
                {hAPICode
                    ? <HilightedCode
                        className="access-by-api-code"
                        language="python"
                        code={hAPICode}
                        theme={themes.oneLight} />
                    : <div className="dataset-loading-container">
                        <div className="dataset-loading-spinner" ></div>
                        <p>Loading...</p>
                      </div>
                }
            </div>
        </div>
    );
}
